import React, { Dispatch, ReactElement, SetStateAction, useState, useEffect } from 'react';
import { TFunction, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LabelMessage from './LabelMessage';
import "./Onboarding.scss";
import Form from './containers/form';
import SubmitButton from './controls/buttons/submitButton';
import EmailField from './controls/fields/emailField';
import { ReactComponent as EmailSentSVG } from "../../node_modules/itd-common/assets/images/onboarding/illus_email.svg";
import { ReactComponent as ErrorSVG } from "../../node_modules/itd-common/assets/images/onboarding/error.svg";
import TextField from './controls/fields/textField';
import Footer from './layout/Footer';
import { ToastContainer, toast } from 'react-toastify';
import CustomReCAPTCHA from "./CustomCaptcha";
import Swal from 'sweetalert2';
import { OnboardingService } from '../services/OnboardingService';
import { Button, Modal } from "react-bootstrap";
import TermsAndContions from './layout/TermsAndCondition'
import {
    IOnboardingQuestionResponse,
    IOnboardingQuestionValidationRequest,
    IOnboardingQuestionValidationResponse,
} from "../models/IOnboardingData";
import { AxiosResponse } from "axios";
import { AuthAsideDefault, AuthAsideMerchantNumber } from '../components/layout/auth';
import SelectField from './controls/fields/selectField';
import { Option } from '../models/Option';
import BrandLogo from '../components/layout/auth/authLogoModal';
import StepProgress, { PageModeOnboarding } from './layout/auth/onboardingSteps';

interface SecurityQuestionSectionProps {
    question: string;
    options: Option[];
    sectionIndex: number
}

interface QuestionOptionProps {
    label: string;
    name: string;
    questionIndex: number
    answerIndex: number
}

interface IProps {
    t: TFunction
}

type RenderObject = {
    title: string,
    renderFunction: () => ReactElement,
    renderAsideFunction?: () => ReactElement,
    initialize?: {
        state: [any, Dispatch<SetStateAction<any>>],
        function: <T>(state: [T, Dispatch<SetStateAction<T>>]) => void
    }
};

type OnboardingFormType = { rut: string, merchantNumber: string, email: string, emailConfirmation: string };
const T_PREFIX: string = 'pages.onBoarding.';
const URUGUAY = "UYU";

const Onboarding: React.FC<IProps> = ({ t }: IProps) => {
    const [pageModeOnboarding, setPageModeOnboarding] = useState<PageModeOnboarding>(PageModeOnboarding.ONBOARDING);
    const [attempsSecurityQuestion, setAttempsSecurityQuestion] = useState('');
    const [securityQuestions, setSecurityQuestions] = useState<IOnboardingQuestionResponse>({ errorLog: "", questions: [], answers: [] });
    const [onBoardingForm, setOnBoardingForm]
        = useState<OnboardingFormType>({ rut: '', merchantNumber: '', email: '', emailConfirmation: '' });
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] = useState<boolean>(false);
    const [userAnswers, setUserAnswers] = useState<IOnboardingQuestionValidationRequest>({ answers: [], merchantNumber: '', fiscalDocument: '', email: '' });
    const [country, setCountry] = useState<string>(URUGUAY);
    const [bgImage, setBgImage] = useState("/bgmerchantcenter.jpg");

    useEffect(() => {
        const updateBg = () => {
            setBgImage(window.innerWidth <= 425 ? "/bgmerchantcentermobile.jpg" : "/bgmerchantcenter.jpg");
        };
        updateBg();
        window.addEventListener("resize", updateBg);
        return () => window.removeEventListener("resize", updateBg);
    }, []);

    const validateEmail = (emailValue: string, emailConfirmation: string): boolean => {
        const emailRegex: RegExp = /\S+@\S+\.\S+/;
        return emailRegex.test(emailValue) && emailConfirmation === emailValue;
    };
    const handleCaptchaVerify = (onVerify: boolean) => {
        setIsCaptchaVerified(onVerify);
    };

    const openTermsAndConditionsModal = () => {
        setCountry(URUGUAY);
        setShowTermsAndConditionsModal(true);
    };

    const closeTermsAndConditionsModal = () => {
        setShowTermsAndConditionsModal(false);
    };

    const getInitialQuestions = async () => {
        try {
            await OnboardingService.getInitialQuestions(onBoardingForm.rut, onBoardingForm.merchantNumber, onBoardingForm.email).then(function (res: AxiosResponse<IOnboardingQuestionResponse>) {
                if (res.data.errorLog != null && res.data.errorLog.length > 0) {
                    Swal.fire({
                        title: 'Error',
                        text: (res != null && res.data != null && res.data.errorLog != null && res.data.errorLog.length > 0) ? t(res.data.errorLog) : 'securityQuestionError',
                        confirmButtonColor: '#f60'
                    }).then(function () {
                        setPageModeOnboarding(PageModeOnboarding.ONBOARDING);
                    });
                    return;
                }
                setSecurityQuestions(res.data);
                let userAnswers: IOnboardingQuestionValidationRequest = { answers: [], merchantNumber: onBoardingForm.merchantNumber, fiscalDocument: onBoardingForm.rut, email: onBoardingForm.email }
                for (var i = 0; i < res.data.answers.length; i++) {
                    userAnswers.answers.push({ respID: (i + 1) + "", selectedResponse: "" })
                }
                setUserAnswers(userAnswers);
                setPageModeOnboarding(PageModeOnboarding.SECURITY_QUESTION);
            });
        } catch (error) {
            console.error("Error al crear el comercio:", error);
            throw new Error("Error al crear el comercio");
        }
    };

    const handleTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTermsAccepted(event.target.checked);
    };

    const validateAnswers = (): boolean => {
        const allQuestionsAnswered = userAnswers.answers.every(answer => answer.selectedResponse.trim() !== '');
        if (!allQuestionsAnswered) {
            toast.error("Por favor, responda todas las preguntas de seguridad.");
            return false;
        }
        return true;
    };

    const SecurityQuestionSection: React.FC<SecurityQuestionSectionProps> = ({ question, options, sectionIndex }) => {
        const selectedValue = userAnswers.answers[sectionIndex]?.selectedResponse || '';

        const selectOptions = [
            { label: 'Seleccionar', value: '', key: 'placeholder', disabled: true },
            ...options.map((option, idx) => ({
                label: option.label,
                value: (idx + 1).toString(),
                key: option.key || option.label,
                disabled: false
            }))
        ];

        const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedValue = event.target.value;
            setUserAnswers((prev) => {
                const updatedAnswers = [...prev.answers];
                updatedAnswers[sectionIndex] = {
                    respID: securityQuestions.questions[sectionIndex].questionId,
                    selectedResponse: selectedValue
                };
                return {
                    ...prev,
                    answers: updatedAnswers
                };
            });
        };

        return (
            <>
                <label className='fs-7'>{question}</label>
                <SelectField
                    className='bg-white fieldQuestion'
                    value={selectedValue}
                    model={[userAnswers, setUserAnswers]}
                    options={selectOptions}
                    label={"Selecciona la correcta"}
                    onChange={handleSelectChange}
                />
            </>
        );
    };

    const ErrorPage: React.FC = () => {
        return (
            <React.Fragment>
                <div className="page mail-key">
                    <div className="">
                        <div className="container-mail">
                            <div className="form-mail">
                                <div>
                                    <ErrorSVG width={150} height={150} />
                                </div>
                                <h2 className='text-check' > {t(`${T_PREFIX}serviceNotAvailable`)}</h2>
                                <span className='merchantInfoText'>{t(`${T_PREFIX}tryLater`)} </span>
                                <Button><Link to={'/login'} className="textBackToLogin"> {t(`${T_PREFIX}tryAgain`)}</Link></Button>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    // ######################################## Render Methods ########################################

    //Render de registro de comercio
    const renderOnBoardingForm = (): ReactElement => {
        const isSubmitDisabled = (): boolean => {
            const isAnyFieldEmpty = !onBoardingForm.rut;
            const isCaptchaNotVerified = !isCaptchaVerified;

            const isEmailInvalid = !(onBoardingForm.email && onBoardingForm.emailConfirmation && onBoardingForm.email === onBoardingForm.emailConfirmation && termsAccepted && validateEmail(onBoardingForm.email, onBoardingForm.emailConfirmation));

            return isAnyFieldEmpty || isCaptchaNotVerified || isEmailInvalid;
        };

        return (
            <React.Fragment>
                <Form model={[onBoardingForm, setOnBoardingForm]} onSubmit={() => setPageModeOnboarding(PageModeOnboarding.MERCHANT_NUMBER)}>
                    <>
                        <div className="row">
                            <div className="col-md-12">
                                <label className='fs-12'>{t(`${T_PREFIX}form.insertFiscalNumber`)}</label>
                                <TextField attr='rut' label={`${T_PREFIX}form.exampleFiscalNumber`} required autoFocus  ></TextField>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <label className='fs-12'>{t(`${T_PREFIX}form.insertEmail`)}</label>
                                <EmailField attr='email' isError={!validateEmail(onBoardingForm.email, onBoardingForm.emailConfirmation)} label={`${T_PREFIX}form.exampleEmail`} required ></EmailField>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <label className='fs-12'>{t(`${T_PREFIX}form.insertRepeatEmail`)}</label>
                                <EmailField attr='emailConfirmation' isError={!validateEmail(onBoardingForm.email, onBoardingForm.emailConfirmation)} label={`${T_PREFIX}form.exampleRepeatEmail`} required></EmailField>
                                <LabelMessage isLabelError={true} valid={validateEmail(onBoardingForm.email, onBoardingForm.emailConfirmation)} message={t(`${T_PREFIX}form.emailNotMatch`)}></LabelMessage>
                            </div>
                        </div>
                        <div>
                            <div className='d-flex align-items-center'>
                                <input type="checkbox" className='cursor-pointer' name="terms" onChange={handleTermsChange} />
                                <small>
                                    {t(`${T_PREFIX}form.acceptTermsAndConditions`)}<Link onClick={openTermsAndConditionsModal} className="linkTo" to={''}><small className='fw-bolder'>{t(`${T_PREFIX}form.termsAndConditions`)}</small></Link>
                                </small>
                            </div>
                        </div>
                        <div className='row' >
                            <div className="col-md-12 col-sm-12 d-flex justify-content-center align-items-center">
                                <CustomReCAPTCHA useCache={false} onVerify={handleCaptchaVerify} />
                            </div>
                            <div className="col-md-12">
                                <div className="d-flex align-content-center justify-content-center">
                                    <SubmitButton className="mb-4 mt-3 sendingButton" disabled={isSubmitDisabled()}>
                                        {t(`${T_PREFIX}continue`)}
                                    </SubmitButton>
                                </div>
                            </div>
                        </div>
                    </>
                </Form>
                <Modal show={showTermsAndConditionsModal} dialogClassName="footer-modal">
                    <div className="modal-content modal-content-demo">
                        <div className="modal-header bg-primary align-items-center">
                            <h4 className="modal-title text-white">{t(`${T_PREFIX}defaultTermsAndConditions`)}</h4>
                            <Button aria-label="Close" className="btn-close p-0" onClick={() => closeTermsAndConditionsModal()}>
                                <span className='text-white' aria-hidden="true">&times;</span>
                            </Button>
                        </div>
                        <div className="modal-body">
                            <div className="scrollable-container">
                                <TermsAndContions country={country} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button onClick={() => closeTermsAndConditionsModal()}>
                                <span className='text-white' aria-hidden="true">{t(`close`)}</span>
                            </Button>
                        </div>
                    </div>
                </Modal>
            </React.Fragment>
        );
    }
    const handleChangeSecurity = async () => {
        try {
            await getInitialQuestions();
        } catch (error) {
            console.error("Error al registrar comercio", error);
            toast.error("Error al registrar comercio");
        }
        setPageModeOnboarding(PageModeOnboarding.SECURITY_QUESTION)
    };

    const renderMerchantNumber = (): ReactElement => {
        const merchantNumberInputIsValid = (): boolean => {
            const regex = /^\d{6,8}$/; // Solo números, entre 6 y 8 dígitos
            if (regex.test(onBoardingForm.merchantNumber)) {
                return true
            } else {
                return false;
            }
        }

        return (
            <React.Fragment>
                <Form model={[onBoardingForm, setOnBoardingForm]} onSubmit={() => handleChangeSecurity()}>
                    <>
                        <div className="row">
                            <div className="col-md-12 my-2">
                                <label>{t(`${T_PREFIX}merchantNumber.insertMerchantNumber`)}</label>
                                <TextField attr='merchantNumber' label={`${T_PREFIX}merchantNumber.exampleMerchantNumber`} required ></TextField>
                                <LabelMessage isLabelError={(onBoardingForm.merchantNumber?.length > 0 && !merchantNumberInputIsValid())} valid={false} message={t(`${T_PREFIX}merchantNumber.merchantNumberValidFormat`)}></LabelMessage>
                            </div>
                        </div>
                        <div className='row'>
                            <div className="col-md-12">
                                <div className="d-flex align-content-center justify-content-center">
                                    <SubmitButton className="mb-4 sendingButton" disabled={!merchantNumberInputIsValid()}>
                                        {t(`${T_PREFIX}continue`)}
                                    </SubmitButton>
                                </div>
                            </div>
                        </div>
                    </>
                </Form>
                <Modal show={showTermsAndConditionsModal} dialogClassName="footer-modal">
                    <div className="modal-content modal-content-demo">
                        <div className="modal-header bg-primary align-items-center">
                            <h4 className="modal-title text-white">{t(`${T_PREFIX}defaultTermsAndConditions`)}</h4>
                            <Button aria-label="Close" className="btn-close p-0" onClick={() => closeTermsAndConditionsModal()}>
                                <span className='text-white' aria-hidden="true">&times;</span>
                            </Button>
                        </div>
                        <div className="modal-body">
                            <div className="scrollable-container">
                                <TermsAndContions country={country} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button onClick={() => closeTermsAndConditionsModal()}>
                                <span className='text-white' aria-hidden="true">{t(`close`)}</span>
                            </Button>
                        </div>
                    </div>
                </Modal>
            </React.Fragment>
        );
    }

    //Render de preguntas de seguridad
    const renderQuestionRequest = (): ReactElement => {
        const handleValidateSecurityQuestions = () => {
            if (!validateAnswers()) {
                return;
            }
            OnboardingService.validateInitialQuestions(userAnswers).then(function (res: AxiosResponse<IOnboardingQuestionValidationResponse>) {
                try {
                    if (Number.parseInt(res.data.returnCode) === 0) {
                        setPageModeOnboarding(PageModeOnboarding.ONBOARDING_RESULT);
                        return;
                    }
                } catch (e) {

                }
                let message = "";
                if (res != null && res.data != null && res.data.errorLog != null && res.data.errorLog.length > 0) {
                    message = res.data.errorLog;
                }

                let errorCounterMessage = "";
                let attempNumber;
                if (res != null && res.data != null && res.data.counter != null && res.data.counter.trim().length > 0) {
                    attempNumber = parseInt(res.data.counter, 10);
                    if (!isNaN(attempNumber) && attempNumber !== 0) {
                        errorCounterMessage = ' Intentos restantes: ' + res.data.counter + ' ';
                    } else {
                        errorCounterMessage = ' Comercio bloqueado ';
                    }
                }
                if (message.length > 0) {
                    const isBlocked = errorCounterMessage.includes('bloqueado');

                    Swal.fire({
                        title: 'Error',
                        text: message,
                        confirmButtonColor: '#f60'
                    }).then(function () {
                        if (isBlocked) {
                            setPageModeOnboarding(PageModeOnboarding.ONBOARDING);
                        }
                    });
                } else {
                    setAttempsSecurityQuestion(errorCounterMessage);
                }
            })
        };
        const buildOptions = (options: string[]): Option[] => {
            return options.map((value: string) => {
                return {
                    label: value,
                    value: value,
                    key: value,
                    disabled: undefined
                }
            })
        }
        return (
            <>
                <div className='row'>
                    <small className='mb-1'>{t(`${T_PREFIX}securityQuestions.description`)}</small>
                </div>
                <div className='row'>
                    <div className="col-md-12 my-2">
                        {securityQuestions.questions.map((questionData, index) => (
                            <SecurityQuestionSection
                                key={index}
                                question={questionData.questionDescription}
                                options={buildOptions(securityQuestions.answers[index])}
                                sectionIndex={index}
                            />
                        ))}
                        <LabelMessage isLabelError={true} valid={(attempsSecurityQuestion === '')} message={attempsSecurityQuestion}></LabelMessage>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <SubmitButton className='sendingButton' onClick={handleValidateSecurityQuestions}>
                            {t(`${T_PREFIX}continue`)}
                        </SubmitButton>
                    </div>
                </div>
            </>
        );
    };

    //Render de registro de correo y aceptación de términos
    const renderOnboardingResult = (): ReactElement => {
        return (
            <React.Fragment>
                <div className="form-mailOnboarding">
                    <div className='svg'>
                        <EmailSentSVG width={300} height={300} />
                    </div>
                    <p className='text-checkOnboarding'> {t(`${T_PREFIX}checkEmail`)}</p>
                    <small className='text-send'>{t(`${T_PREFIX}sendEmail`)} </small>
                </div>
            </React.Fragment>
        );
    }

    //###############################################################################################################################
    const buildRenderObject = (
        title: string,
        renderFunction: () => ReactElement,
        renderAsideFunction?: () => ReactElement,
        initialize?: { state: [any, Dispatch<SetStateAction<any>>], function: <T>(state: [T, Dispatch<SetStateAction<T>>]) => void }
    ): RenderObject => {
        return { title, renderFunction, renderAsideFunction, initialize };
    }

    const buildTitle = (sufix: string, options?: any): string => {
        return t(`${T_PREFIX}${sufix}`, options);
    }

    const renderObjectsMap: Map<PageModeOnboarding, RenderObject> = new Map([
        [
            PageModeOnboarding.ONBOARDING,
            buildRenderObject(
                buildTitle('title'),
                renderOnBoardingForm,
                () => <AuthAsideDefault />
            )
        ],
        [
            PageModeOnboarding.MERCHANT_NUMBER,
            buildRenderObject(
                buildTitle('merchantNumber.title'),
                renderMerchantNumber,
                () => <AuthAsideMerchantNumber />
            )
        ],
        [
            PageModeOnboarding.SECURITY_QUESTION,
            buildRenderObject(
                buildTitle('securityQuestions.title'),
                renderQuestionRequest,
                () => <AuthAsideDefault />
            )
        ],
        [
            PageModeOnboarding.ONBOARDING_RESULT,
            buildRenderObject(
                buildTitle('title'),
                renderOnboardingResult,
                () => <AuthAsideDefault />
            )
        ]
    ]);

    const getRenderObjectByPageModeOnboarding = (pageModeOnboarding: PageModeOnboarding): RenderObject => {
        return renderObjectsMap.get(pageModeOnboarding) || { title: "", renderFunction: () => <></> } as RenderObject;
    }
    const renderTitle = () => {
        return getRenderObjectByPageModeOnboarding(pageModeOnboarding).title;
    }
    const renderByPageModeOnboarding = () => {
        try {
            return (
                <>
                    {getRenderObjectByPageModeOnboarding(pageModeOnboarding).renderFunction()}
                </>
            );
        } catch (error) {
            return <ErrorPage />;
        }
    };
    const renderAsideByPageModeOnboarding = () => {
        return (
            <>
                {getRenderObjectByPageModeOnboarding(pageModeOnboarding).renderAsideFunction?.()}
            </>
        )
    }

    return (
        <React.Fragment>
            <ToastContainer autoClose={3000} ></ToastContainer>
            <div className="login">
                <div className="page">
                    <div className="container-login100" style={{ backgroundImage: `url(${bgImage})` }}>
                        <div className="wrap-login100 onboardingForm">
                            <BrandLogo />
                            <StepProgress pageMode={pageModeOnboarding} />
                            <span className="login100-form-title text-start my-2">
                                {pageModeOnboarding !== PageModeOnboarding.ONBOARDING_RESULT &&
                                    renderTitle()
                                }
                            </span>
                            <div>
                                {renderByPageModeOnboarding()}
                                {
                                    pageModeOnboarding === PageModeOnboarding.ONBOARDING &&
                                    <p className='registerMerchant'> {t(`${T_PREFIX}alreadyMerchant`)} <Link to={'/login'} className='linkTo'>{t(`${T_PREFIX}enterPortal`)}</Link> </p>
                                }
                            </div>
                            <div className="row">
                                <div className="col-md-12 d-flex justify-content-center mt-4">
                                    <Link to={'/help-outside'} className="linkTo">
                                        {t(`${T_PREFIX}needHelp`)}
                                    </Link>
                                </div>
                            </div>
                        </div>
                        {renderAsideByPageModeOnboarding()}
                    </div>
                </div>
                <Footer isValid={false} />
            </div>
        </React.Fragment>
    )
}
export default withTranslation()(Onboarding);
